// src/components/layout/ReviewRequestPopup.tsx
// Stacked, persistent "please review this" cards — pushed onto the queue by
// useSocket.ts whenever someone submits a task for the current user's review.
// Unlike the generic toast, these don't auto-dismiss and carry an inline
// Complete action so the assigner can approve without leaving the page.
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Eye, Loader2 } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { RootState } from 'store';
import { removeReviewRequest } from 'store/slices/reviewRequestSlice';
import { showToast } from 'store/slices/toastSlice';
import { taskService } from 'services/taskService';

export default function ReviewRequestPopup() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const requests = useSelector((s: RootState) => s.reviewRequest);

  const approve = useMutation({
    mutationFn: (taskId: string) => taskService.review(taskId, { decision: 'approve' }),
    onSuccess: (_data, taskId) => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      dispatch(removeReviewRequest(taskId));
      dispatch(showToast('✅ Task marked completed'));
    },
    onError: () => dispatch(showToast('❌ Failed to complete task')),
  });

  if (requests.length === 0) return null;

  return (
    <div className="fixed bottom-24 left-6 z-50 flex flex-col-reverse gap-2.5 w-80 max-w-[calc(100vw-3rem)]">
      {requests.map(r => (
        <div key={r.taskId} className="bg-[var(--om-card)] border border-green-500/30 rounded-2xl shadow-2xl p-4">
          <div className="flex items-start gap-2.5 mb-3">
            <span className="w-8 h-8 rounded-full bg-gradient-to-br from-green-400 to-green-600 shadow-sm shadow-green-500/40 flex items-center justify-center flex-shrink-0">
              <CheckCircle2 className="w-4 h-4 text-white" strokeWidth={2} />
            </span>
            <div className="min-w-0">
              <p className="text-sm font-bold text-[var(--om-text)] leading-snug">
                {r.fromName} completed "{r.title}"
              </p>
              <p className="text-[11px] text-[var(--om-text-dim)] mt-0.5">Please review and mark it complete.</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => navigate(`/task/${r.taskId}`)}
              style={{ cursor: 'pointer' }}
              className="inline-flex items-center justify-center gap-1.5 flex-1 py-2 bg-[var(--om-panel)] border border-[var(--om-border)] rounded-lg text-[12px] text-[var(--om-text-muted)] hover:text-[var(--om-text)] font-semibold transition-all"
            >
              <Eye className="w-3.5 h-3.5" strokeWidth={2} /> View
            </button>
            <button
              onClick={() => approve.mutate(r.taskId)}
              disabled={approve.isPending}
              style={{ cursor: 'pointer' }}
              className="inline-flex items-center justify-center gap-1.5 flex-1 py-2 bg-green-600 hover:bg-green-500 disabled:opacity-60 rounded-lg text-white text-[12px] font-bold transition-all"
            >
              {approve.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2} />} Complete
            </button>
            <button
              onClick={() => dispatch(removeReviewRequest(r.taskId))}
              style={{ cursor: 'pointer' }}
              title="Dismiss"
              className="w-8 h-8 flex-shrink-0 rounded-lg text-[var(--om-text-dim)] hover:text-[var(--om-text)] hover:bg-[var(--om-overlay-10)] transition-all"
            >
              ×
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
